import { Link, useParams } from "react-router-dom"
import { ChevronLeft, FileUp } from "lucide-react"
import { ACTIVE_STATUSES, useLeaseIntake } from "@/app/api/leaseIntakes"
import { useClientTheme } from "@/app/components/ThemeContext"
import { PageHeader } from "@/app/components/PageHeader"
import { ErrorBanner, ReadOnlyCard, formatApiError } from "@/app/components/FormPrimitives"
import { useCurrentUser } from "@/app/hooks/useCurrentUser"
import type { ClientTheme } from "@/app/lib/themes"
import { UploadPanel } from "@/app/pages/LeaseIntakePage/UploadPanel"
import { FailedPanel, ProgressPanel } from "@/app/pages/LeaseIntakePage/ProgressPanel"
import { ReviewPanel } from "@/app/pages/LeaseIntakePage/ReviewPanel"
import { CommittedPanel } from "@/app/pages/LeaseIntakePage/CommittedPanel"
import { StatusBadge, mutedText } from "@/app/pages/LeaseIntakePage/shared"

const TITLE = "Import Leases from Document"
const SUBTITLE = "Extract leases from a recorded Assignment, lease, or CSV into the Lease Schedule"

// `leases/intake` → upload + recent runs; `leases/intake/:id` → one run,
// rendered by status (progress → review → committed / failed / discarded).
// Owner/superadmin only — the same predicate as the server's authorize_manage!.
export function LeaseIntakePage() {
  const { theme } = useClientTheme()
  const { id } = useParams<{ id: string }>()
  const { canManageUsers, isLoading: userLoading } = useCurrentUser()

  if (userLoading) {
    return (
      <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
        <div style={mutedText(theme, "13px")}>Loading…</div>
      </div>
    )
  }

  if (!canManageUsers) {
    return (
      <ReadOnlyCard
        theme={theme}
        title={TITLE}
        subtitle={SUBTITLE}
        icon={<FileUp size={17} strokeWidth={1.75} />}
      />
    )
  }

  return (
    <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
      <div className={id ? "" : "max-w-[900px]"}>
        <BackLink to={id ? "/leases/intake" : "/leases"} theme={theme}>
          {id ? "All imports" : "Lease Schedule"}
        </BackLink>
        <PageHeader
          title={TITLE}
          subtitle={SUBTITLE}
          theme={theme}
          icon={<FileUp size={17} strokeWidth={1.75} />}
        />
        {id ? <RunView id={id} theme={theme} /> : <UploadPanel theme={theme} />}
      </div>
    </div>
  )
}

function RunView({ id, theme }: { id: string; theme: ClientTheme }) {
  const { data: run, isLoading, isError, error } = useLeaseIntake(id)

  if (isLoading) return <div style={mutedText(theme, "13px")}>Loading…</div>
  if (isError || !run) {
    return <ErrorBanner theme={theme} message={formatApiError(error ?? new Error("Not found"))} />
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <span
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "14px",
            fontWeight: 600,
            color: theme.textPrimary,
          }}
        >
          {run.filename}
        </span>
        <StatusBadge status={run.status} theme={theme} />
        <span style={mutedText(theme)}>
          {run.doc_kind.toUpperCase()}
          {run.page_count != null && ` · ${run.page_count} page${run.page_count === 1 ? "" : "s"}`}
        </span>
      </div>

      {ACTIVE_STATUSES.includes(run.status) ? (
        <ProgressPanel run={run} theme={theme} />
      ) : run.status === "failed" ? (
        <FailedPanel run={run} theme={theme} />
      ) : run.status === "review" ? (
        <ReviewPanel run={run} theme={theme} />
      ) : (
        <CommittedPanel run={run} theme={theme} />
      )}
    </div>
  )
}

function BackLink({
  to,
  theme,
  children,
}: {
  to: string
  theme: ClientTheme
  children: React.ReactNode
}) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 mb-3"
      style={{
        fontFamily: "var(--font-ui)",
        fontSize: "12px",
        color: theme.textMuted,
        textDecoration: "none",
      }}
    >
      <ChevronLeft size={13} /> {children}
    </Link>
  )
}
